import React, { useEffect, useState, useCallback } from "react"
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import Sidebar from "./components/Sidebar"
import GetStarted from "./pages/GetStarted"
import DailyCalendar from "./pages/DailyCalendar"
import AnalyzeSchedule from "./pages/AnalyzeSchedule"
import FutureHuddle from "./pages/FutureHuddle"
import ScheduleTemplates from "./pages/ScheduleTemplates"
import KPIDashboard from "./pages/KPIDashboard"
import Settings from "./pages/Settings"
import Support from "./pages/Support"
import SubmitTicket from "./pages/SubmitTicket"
import ArticleDetail from "./pages/ArticleDetail"
import AllPosts from "./pages/AllPosts"
import Authorize from "./pages/Authorize"
import Notifications from "./pages/Notifications"
import NurseView from "./pages/NurseView"
import ErrorPage from "./pages/ErrorPage"
import Welcome from "./pages/Welcome"
import { AuthenticationGuard } from "./components/Authentication/AuthenticationGuard"
import FeedbackForm from "./components/Feedback/FeedbackForm" // Import the FeedbackForm component
import { PageLoader } from "./components/Authentication/page-loader" // Adjust the import path as necessary
import TermsConditions from "./pages/TermsConditions"
import NotificationPopup from "./components/DailyCalendarFiles/CustomCalendar/NotificationPopup"
import { ErrorBoundary } from "react-error-boundary"
import ErrorThrowingComponent from "./components/ErrorThrowingComponent" // Import the error throwing component
import WhatsNew from "./pages/WhatsNew"

import { usePostHog } from "posthog-js/react"

import "posthog-js/dist/recorder"
import "posthog-js/dist/surveys"
import "posthog-js/dist/exception-autocapture"
import "posthog-js/dist/tracing-headers"
import "posthog-js/dist/web-vitals"
import useLogging from "./hooks/useLogging"
import { PiAlignCenterVerticalSimpleDuotone } from "react-icons/pi"

function App() {
	const { user, isLoading, isAuthenticated, loginWithRedirect } = useAuth0()

	const navigate = useNavigate()
	const location = useLocation()
	const [showFeedbackForm, setShowFeedbackForm] = useState(false)
	const [notification, setNotification] = useState({ visible: false, message: "", success: true })
	const [renderedHomeComponent, setRenderedHomeComponent] = useState("none")

	const logging = useLogging()

	//posthog user identification
	const posthog = usePostHog()
	useEffect(() => {
		if (user) {
			posthog.identify(user.sub, {
				email: user.email,
				name: user.name
			})
			posthog.group(user.organization_id)
		}
		// `posthog` is initialized once at module load and remains stable.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user])

	useEffect(() => {
		// posthog.capture('$pageview', { page: pageName });
	}, [location])

	// Handle page visibility change and beforeunload
	// useEffect(() => {
	//   const handleVisibilityChange = () => {
	//     if (
	//       document.visibilityState === 'hidden' &&
	//       isAuthenticated &&
	//       location.pathname !== '/' &&
	//       shouldShowFeedback()
	//     ) {
	//       setShowFeedbackForm(true);
	//     }
	//   };

	//   const handleBeforeUnload = (e) => {
	//     if (isAuthenticated && location.pathname !== '/' && shouldShowFeedback()) {
	//       setShowFeedbackForm(true);
	//       e.preventDefault();
	//       e.returnValue = ''; // This is required for Chrome
	//     }
	//   };

	//   // Add event listeners
	//   document.addEventListener('visibilitychange', handleVisibilityChange);
	//   window.addEventListener('beforeunload', handleBeforeUnload);

	//   // Cleanup
	//   return () => {
	//     document.removeEventListener('visibilitychange', handleVisibilityChange);
	//     window.removeEventListener('beforeunload', handleBeforeUnload);
	//   };
	// }, [isAuthenticated, location.pathname]);

	// Check for end of day feedback
	// useEffect(() => {
	//   const checkEndOfDay = () => {
	//     if (
	//       isAuthenticated &&
	//       location.pathname !== '/' &&
	//       shouldShowFeedback()
	//     ) {
	//       setShowFeedbackForm(true);
	//     }
	//   };

	//   // Check when component mounts and when route changes
	//   checkEndOfDay();

	//   // Set up interval to check every 5 minutes
	//   const interval = setInterval(checkEndOfDay, 5 * 60 * 1000);

	//   return () => clearInterval(interval);
	// }, [isAuthenticated, location.pathname]);

	const [sidebarOpen, setSidebarOpen] = useState(true)


	const createLogMessage = useCallback(
		(eventType, description) => {
			const timestamp = new Date().toISOString()
			const userId = user ? user.sub : "unknown" // TODO: Check if this is correct

			return `${timestamp} | ${eventType} | userID: ${userId} | ${description}`
		},
		[user]
	) // Dependency array includes `user` since it's used inside the function

	useEffect(() => {
		if (!isLoading && isAuthenticated) {
			// Check if the user was redirected with a `returnTo` path
			const appState = location.state?.appState
			if (appState?.returnTo) {
				navigate(appState.returnTo) // Redirect to the intended path
			}
		} else if (!isLoading && !isAuthenticated && location.pathname !== "/") {
			loginWithRedirect({
				appState: { returnTo: location.pathname } // Store the current path in `returnTo`
			})
		}
	}, [isLoading, isAuthenticated, navigate, location, loginWithRedirect])

	useEffect(() => {
		posthog.capture("$pageview", { page: location.pathname })

		const logPageVisit = async () => {
			const pageName =
				location.pathname === "/"
					? "Get Started Page"
					: location.pathname.replace("/", "").replace("-", " ") + " Page"
			try {
				logging.info(`Page Visited: ${pageName}`, {
					path: PiAlignCenterVerticalSimpleDuotone,
					pathName: location.pathname
				})
			} catch (error) {
				logging.error("Error logging page visit:", {
					error: error.message,
					stack: error.stack
				})
			}
		}

		logPageVisit()
		// `logging` is a fresh object each render (useLogging returns inline object); adding it would re-run every render. `posthog` is initialized once at module load and remains stable.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, createLogMessage])

	const handleFeedbackSubmit = async (feedbackData, token) => {
		try {
			const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feedback`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify(feedbackData)
			})

			if (!response.ok) {
				throw new Error("Failed to submit feedback")
			}

			const result = await response.json()
			logging.info("Feedback submitted successfully:", {
				result: result
			})

			// Store in localStorage to prevent showing again today
			const today = new Date().toDateString()
			localStorage.setItem("feedbackCompletedToday", today)

			// Show success notification
			setNotification({
				visible: true,
				message: "Feedback submitted successfully!",
				success: true
			})

			// Close the feedback form
			setShowFeedbackForm(false)
		} catch (error) {
			logging.error("Error submitting feedback:", {
				error: error.message,
				stack: error.stack
			})
			// Show error notification
			setNotification({
				visible: true,
				message: "Error submitting feedback. Please try again.",
				success: false
			})
		}

		// Hide notification after 3 seconds
		setTimeout(() => {
			setNotification({ visible: false, message: "", success: true })
		}, 3000)
	}

	useEffect(() => {
		if (isAuthenticated && user) {
			// Initialize Pylon chat settings with the authenticated user's details
			window.pylon = {
				chat_settings: {
					app_id: "830bc5f7-26c1-4d82-b15a-85cc26ffb406",
					email: user.email,
					name: user.name,
					avatar_url: user.picture
				}
			}
		}
	}, [isAuthenticated, user])

	useEffect(() => {
		logging.info("rendered home component", renderedHomeComponent)
		// `logging` is a fresh object each render (useLogging returns inline object); adding it would re-run every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [renderedHomeComponent])

	const loadingState = location.pathname !== "/" && isLoading

	if (loadingState) {
		return <PageLoader />
	}

	return (
		<ErrorBoundary
			FallbackComponent={() => <ErrorPage genericError={true} />}
			onError={(error, errorInfo) => {
				// Log all errors caught by ErrorBoundary to GCP
				logging.error("React ErrorBoundary caught an error", {
					error: error.message,
					stack: error.stack,
					errorInfo: errorInfo,
					component: "ErrorBoundary",
					// Check if it's a schema validation error
					isSchemaError:
						error.message?.includes("validation") ||
						error.name === "ZodError" ||
						error.stack?.includes("ScheduleSchema"),
					url: location.pathname,
					userId: user?.sub
				})

				posthog.capture("error-page-loaded")
			}}
		>
			<div className="App">
				{location.pathname !== "/" && (
					<Sidebar
						renderedHomeComponent={renderedHomeComponent}
						sidebarOpen={sidebarOpen}
						setSidebarOpen={setSidebarOpen}
					/>
				)}

				{notification.visible && (
					<NotificationPopup
						message={notification.message}
						success={notification.success}
					/>
				)}

				<Routes>
					<Route path="/" element={<GetStarted />} />
					<Route path="/authorize" element={<Authorize />} />
					<Route
						path="/terms-and-conditions"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <TermsConditions sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:settings"]}
							/>
						}
					/>
					<Route
						path="/home"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => (
									<Welcome
										setRenderedHomeComponent={setRenderedHomeComponent}
										sidebarOpen={sidebarOpen}
									/>
								)}
								allowedPermissions={["read:settings"]}
							/>
						}
					/>
					<Route
						path="/analyze-schedule"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <AnalyzeSchedule sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:daily-huddle"]}
							/>
						}
					/>
					<Route
						path="/future-huddle"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <FutureHuddle sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:future-huddle"]}
							/>
						}
					/>
					<Route
						path="/ic-tracker"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <DailyCalendar sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:ic-tracker"]}
							/>
						}
					/>
					<Route
						path="/schedule-templates"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <ScheduleTemplates sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:templates"]}
							/>
						}
					/>
					<Route
						path="/executive-reports"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <KPIDashboard sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:kpi-dashboard"]}
							/>
						}
					/>
					{/* <Route path="/nursing-leadership-view" element={<AuthenticationGuard component={NursingLeadershipView} allowedPermissions={['read:kpi-dashboard']} />} /> */}
					<Route
						path="/settings"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <Settings sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:settings"]}
							/>
						}
					/>
					<Route
						path="/support"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <Support sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:support"]}
							/>
						}
					/>
					<Route
						path="/submit-ticket"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <SubmitTicket sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:support"]}
							/>
						}
					/>
					<Route
						path="/notifications"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <Notifications sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:notifications"]}
							/>
						}
					/>
					<Route
						path="/support/articles/:id"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <ArticleDetail sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:support"]}
							/>
						}
					/>
					<Route
						path="/support/articles"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <AllPosts sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:support"]}
							/>
						}
					/>
					<Route
						path="/nurse-view"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <NurseView sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:schedule-personal"]}
							/>
						}
					/>
					<Route path="/error" element={<ErrorPage />} />{" "}
					<Route path="/error-throwing" element={<ErrorThrowingComponent />} />
					<Route
						path="/whats-new"
						element={
							<AuthenticationGuard
								sidebarOpen={sidebarOpen}
								component={() => <WhatsNew sidebarOpen={sidebarOpen} />}
								allowedPermissions={["read:settings"]}
							/>
						}
					/>
					<Route path="*" element={<Navigate to="/" />} />
				</Routes>

				{isAuthenticated && location.pathname !== "/" && (
					<FeedbackForm
						isOpen={showFeedbackForm}
						onClose={() => setShowFeedbackForm(false)}
						onSubmit={handleFeedbackSubmit}
					/>
				)}
			</div>
		</ErrorBoundary>
	)
}

export default App
